import React from "react";
import { Box, Text } from "@mantine/core";
import styles from "./sales.module.css";

/**
 * Canonical status keys shared by both Sales screens. Quiet default (dot +
 * muted label) for the norm (`closed` / `captured`); soft-tint pill for the
 * exceptions. Colour marks exceptions only — there is deliberately no green
 * "success" pill.
 */
export type SalesStatusKey =
  | "closed"
  | "captured"
  | "open"
  | "partial"
  | "refunded"
  | "voided";

const LABELS: Record<SalesStatusKey, string> = {
  closed: "Closed",
  captured: "Captured",
  open: "Open",
  partial: "Partial",
  refunded: "Refunded",
  voided: "Voided",
};

const PILL_CLASS: Partial<Record<SalesStatusKey, string>> = {
  open: styles.pillOpen,
  partial: styles.pillPartial,
  refunded: styles.pillRefunded,
  voided: styles.pillVoided,
};

/**
 * Map our data to a status key.
 *  - Transactions: transaction_status `void` → voided (wins over refunds);
 *    refund_status `full` → refunded, `partial` → partial; else
 *    transaction_status `paid` → closed, otherwise → open.
 */
export function transactionStatusKey(
  transactionStatus: string | undefined,
  refundStatus: string | null | undefined,
): SalesStatusKey {
  if (transactionStatus === "void") return "voided";
  if (refundStatus === "full") return "refunded";
  if (refundStatus === "partial") return "partial";
  return transactionStatus === "paid" ? "closed" : "open";
}

interface StatusCellProps {
  status: SalesStatusKey;
}

export default function StatusCell({ status }: StatusCellProps) {
  const pillClass = PILL_CLASS[status];
  if (!pillClass) {
    // Quiet default: 6px grey dot + muted label, no pill.
    return (
      <Box className={styles.statusDot}>
        <Text component="span" className={styles.statusDotLabel}>
          {LABELS[status]}
        </Text>
      </Box>
    );
  }
  return (
    <Box component="span" className={`${styles.statusPill} ${pillClass}`}>
      {LABELS[status]}
    </Box>
  );
}
